import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import BookingFilters from "./BookingFilters";
import BookingsTable from "./BookingsTable";
import BookingCard from "./BookingCard";
import BookingDetailDrawer from "./BookingDetailDrawer";
import { apiRequest } from "@/lib/queryClient";
import type { BookingResponseUI } from "@/types/activities";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BookingsTabProps {
  organizationId: string;
}

// Types for booking management
type ViewMode = 'table' | 'card';
type BookingType = 'all' | 'marketplace';
type BookingStatus = 'all' | 'pending' | 'negotiating' | 'awaiting-approval' | 'vendor-booked' | 'confirmed' | 'cancelled' | 'rejected' | 'expired';

interface BookingFilters {
  search: string;
  organization: string;
  type: BookingType;
  status: BookingStatus;
}



export default function BookingsTab({ organizationId }: BookingsTabProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [filters, setFilters] = useState<BookingFilters>({
    search: '',
    organization: 'all',
    type: 'all',
    status: 'all'
  });
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<BookingResponseUI | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch bookings for this organization
  const { data: bookingsData = [], isLoading, error, refetch } = useQuery<BookingResponseUI[]>({
    queryKey: [`/bookings?organizationId=${organizationId}`],
    queryFn: () => apiRequest<BookingResponseUI[]>('GET', `/bookings?organizationId=${organizationId}`),
    enabled: !!organizationId
  });

  // Use the data directly as it's already in the correct format
  const bookings = bookingsData;

  // Filter bookings based on current filters
  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const matchesSearch = booking.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                          booking.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
                          booking.organizationName.toLowerCase().includes(filters.search.toLowerCase());
      const matchesOrganization = filters.organization === 'all' || booking.organizationName === filters.organization;
      const matchesType = filters.type === 'all' || booking.type === filters.type;
      const matchesStatus = filters.status === 'all' || booking.status === filters.status;
      
      return matchesSearch && matchesOrganization && matchesType && matchesStatus;
    });
  }, [bookings, filters]);

  const handleSelectBooking = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedBookings([...selectedBookings, id]);
    } else {
      setSelectedBookings(selectedBookings.filter(bookingId => bookingId !== id));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedBookings(filteredBookings.map(booking => booking.id));
    } else {
      setSelectedBookings([]);
    }
  };

  const openBookingDrawer = (booking: BookingResponseUI) => {
    setSelectedBooking(booking);
    setDrawerOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
          <p className="text-muted-foreground">Loading bookings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="athlyn-card">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4"/>
          <h3 className="font-semibold mb-2">Failed to load bookings</h3>
          <p className="text-muted-foreground mb-4">Unable to fetch bookings</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (bookings.length === 0) {
    return (
      <Card className="athlyn-card">
        <CardContent className="p-12 text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4"/>
          <h3 className="text-lg font-semibold mb-2">No activity bookings</h3>
          <p className="text-muted-foreground">This client has no activity bookings yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Filters and Controls */}
        <BookingFilters
          filters={filters}
          onFiltersChange={setFilters}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedCount={selectedBookings.length}
          onBulkAction={(action) => console.log('Bulk action:', action)}
        />

        {/* Content View */}
        {filteredBookings.length > 0 ? (
          viewMode === 'table' ? (
            <BookingsTable
              bookings={filteredBookings}
              selectedBookings={selectedBookings}
              onSelectBooking={handleSelectBooking}
              onSelectAll={handleSelectAll}
              onOpenBooking={openBookingDrawer}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBookings.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  selected={selectedBookings.includes(booking.id)}
                  onSelect={(selected) => handleSelectBooking(booking.id, selected)}
                  onOpen={() => openBookingDrawer(booking)}
                />
              ))}
            </div>
          )
        ) : (
          // No results after filtering
          <Card className="athlyn-card">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4"/>
              <h3 className="text-lg font-semibold mb-2">No matching bookings</h3>
              <p className="text-muted-foreground">No bookings match your current filters.</p>
              <Button variant="outline" onClick={() => setFilters({
                search: '',
                organization: 'all',
                type: 'all',
                status: 'all'
              })}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Booking Detail Drawer */}
      <BookingDetailDrawer
        booking={selectedBooking?.booking ?? null}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </>
  );
}